/**
 * Release 2 A — the read-only grid shared by smart boards (S3) and system boards (S4). There is no membership behind
 * these listings: no upload or drop target, no manual order, no "remove from board"; cards can still be dragged
 * out to real boards, opened, selected and acted on (bulk toolbar / context menu without a board).
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { UseInfiniteQueryResult } from '@tanstack/react-query';
import { Images, SlidersHorizontal } from 'lucide-react';
import type { AssetCard, Facets } from '@/lib/api/types';
import { useSelection } from '@/stores/selection';
import { EmptyState, Spinner } from '@/components/Misc';
import { Button } from '@/components/Button';
import { Select } from '@/components/Input';
import { MasonryGrid } from '@/features/grid/MasonryGrid';
import { isPlaceholder, toGridItems, type BoardCardEntry, type GridItem } from '@/features/grid/types';
import { BulkToolbar } from '@/features/grid/BulkToolbar';
import { GridContextMenu } from '@/features/grid/GridContextMenu';
import { QuickLook } from '@/features/grid/QuickLook';
import { FilterBar } from '@/features/grid/FilterBar';
import { useAssetActions } from '@/features/grid/useAssetActions';
import { isApiError } from '@/lib/api/errors';
import { plural } from '@/lib/format';
import { countBoardFilters, decodeBoardFilters, encodeBoardFilters, filtersQueryParam, EMPTY_BOARD_FILTERS, type BoardFilters } from '@/features/search/predicates/serialize';
import { SMART_SORTS, type SmartSort } from './definition';

/** URL state of a virtual board page: `?f=` caller filters (same encoding as boards), `?sort=`. */
export function useVirtualBoardUrl(defaultSort: SmartSort) {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => decodeBoardFilters(params.get('f')), [params]);
  const raw = params.get('sort');
  const sort = (SMART_SORTS.some((s) => s.id === raw) ? raw : defaultSort) as SmartSort;
  const setFilters = useCallback(
    (f: BoardFilters) => {
      const next = new URLSearchParams(params);
      const enc = encodeBoardFilters(f);
      if (enc) next.set('f', enc);
      else next.delete('f');
      setParams(next, { replace: true });
    },
    [params, setParams],
  );
  const setSort = useCallback(
    (s: SmartSort) => {
      const next = new URLSearchParams(params);
      if (s === defaultSort) next.delete('sort');
      else next.set('sort', s);
      setParams(next, { replace: true });
    },
    [params, setParams, defaultSort],
  );
  return { filters, filtersParam: filtersQueryParam(filters), sort, setFilters, setSort };
}

/** A smart or system board page (`BoardAssetsPage`; the smart listing goes through the board query, which types placeholders in). */
type VirtualPage = { items: BoardCardEntry[]; total?: number | null; facets?: Facets };

export type VirtualBoardViewProps = {
  title: ReactNode;
  /** small inline badge / icon after the title */
  badge?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  listing: UseInfiniteQueryResult<{ pages: VirtualPage[] }>;
  filters: BoardFilters;
  onFilters: (f: BoardFilters) => void;
  filtersOpen: boolean;
  onToggleFilters: (open: boolean) => void;
  sort: SmartSort;
  onSort: (s: SmartSort) => void;
  emptyTitle: string;
  emptyBody?: ReactNode;
  label: string;
};

export function VirtualBoardView({ title, badge, description, actions, listing, filters, onFilters, filtersOpen, onToggleFilters, sort, onSort, emptyTitle, emptyBody, label }: VirtualBoardViewProps) {
  const { ws = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const cards = useMemo(() => listing.data?.pages.flatMap((p) => p.items.filter((c): c is AssetCard => !isPlaceholder(c))) ?? [], [listing.data]);
  const items = useMemo(() => toGridItems(cards), [cards]);
  const first = listing.data?.pages[0];
  const total = first?.total ?? null;
  const facets = first?.facets;
  const nActive = countBoardFilters(filters);
  const { run, dialogs } = useAssetActions(null);
  const [ctx, setCtx] = useState<{ at: { x: number; y: number }; card: AssetCard } | null>(null);
  const [quick, setQuick] = useState<AssetCard | null>(null);
  const clearSelection = useSelection((s) => s.clear);
  useEffect(() => () => clearSelection(), [clearSelection]);
  const open = useCallback((item: GridItem) => navigate(`/w/${ws}/a/${item.card.id}`, { state: { background: location.pathname + location.search } }), [navigate, ws, location.pathname, location.search]);
  const onContext = useCallback((at: { x: number; y: number }, item: GridItem) => setCtx({ at, card: item.card as AssetCard }), []);
  const onQuickLook = useCallback((item: GridItem | null) => setQuick(item ? (item.card as AssetCard) : null), []);
  const fetchNext = useCallback(() => void listing.fetchNextPage(), [listing]);

  if (listing.isError) {
    const e = listing.error;
    return <EmptyState title={isApiError(e) && e.status === 404 ? 'Not found' : 'Could not load assets'} body={isApiError(e) ? e.message : undefined} action={<Button onClick={() => void listing.refetch()}>Retry</Button>} />;
  }

  return (
    <div className="p-4 md:p-6" data-virtual-board>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h1 className="text-lg font-semibold">{title}</h1>
        {badge}
        {total != null && <span className="text-sm text-fg-subtle" data-virtual-total>{plural(total, 'asset', 'assets')}</span>}
        {description && <span className="truncate text-sm text-fg-muted">— {description}</span>}
        <span className="ml-auto flex items-center gap-1.5">
          {actions}
          <Button size="sm" variant={filtersOpen ? 'secondary' : 'ghost'} onClick={() => onToggleFilters(!filtersOpen)} aria-pressed={filtersOpen} data-toggle-filters>
            <SlidersHorizontal size={13} /> Filters{nActive ? ` (${nActive})` : ''}
          </Button>
          <label className="flex items-center gap-1 text-xs text-fg-muted">
            sort
            <Select value={sort} onChange={(e) => onSort(e.target.value as SmartSort)} className="h-7 w-auto py-0 text-xs" aria-label="Sort">
              {SMART_SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </Select>
          </label>
        </span>
      </div>
      {filtersOpen && <FilterBar filters={filters} onChange={onFilters} facets={facets} total={total} loading={listing.isFetching && !listing.isFetchingNextPage} saveAsSmart={false} />}
      {listing.isPending ? (
        <div className="flex justify-center py-16 text-fg-muted"><Spinner size={20} /></div>
      ) : (
        <MasonryGrid
          items={items}
          hasNextPage={listing.hasNextPage}
          isFetchingNextPage={listing.isFetchingNextPage}
          fetchNextPage={fetchNext}
          onOpen={open}
          onQuickLook={onQuickLook}
          onContextMenu={onContext}
          draggable
          label={label}
          emptyState={nActive ? <EmptyState icon={<Images size={28} />} title="Nothing matches these filters" body="Try removing a filter." action={<Button size="sm" onClick={() => onFilters({ ...EMPTY_BOARD_FILTERS })}>Clear filters</Button>} /> : <EmptyState icon={<Images size={28} />} title={emptyTitle} body={emptyBody} />}
          footer={listing.isFetchingNextPage ? <div className="flex justify-center py-4 text-fg-muted"><Spinner /></div> : null}
        />
      )}
      <BulkToolbar boardId={null} run={run} />
      {ctx && <GridContextMenu at={ctx.at} card={ctx.card} boardId={null} onClose={() => setCtx(null)} run={run} />}
      {quick && <QuickLook card={quick} onClose={() => setQuick(null)} onOpen={() => (setQuick(null), open({ key: quick.id, aspect: 1, card: quick }))} />}
      {dialogs}
    </div>
  );
}
